import React, { useEffect, useState } from 'react';
import { 
  Search, Book, Code, Database, Cloud, Shield, Zap, LogOut, Menu, X, 
  ChevronRight, ChevronDown, ExternalLink, Star, GitBranch, Users, 
  FileText, Sparkles, Folder, FolderOpen, File, Plus, Edit, Trash2, 
  Save, Eye, EyeOff, Clock, User, ArrowLeft 
} from 'lucide-react';
import MarkdownEditor from '../components/editor/MarkdownEditor';
import DocumentViewer from '../components/documents/DocumentViewer';
import { foldersService } from '../services/folders.service';
import { documentsService } from '../services/documents.service';

// Componente principal de documentación
const DocumentationPage = ({ user, onLogout }) => {
  const safeFormatDate = (value) => {
    if (!value) return '—';
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [folders, setFolders] = useState([]);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [creatingDefaults, setCreatingDefaults] = useState(false);
  const [totalDocuments, setTotalDocuments] = useState(0);

  const defaultFoldersSpec = [
    { name: 'Visión y Estrategia', color: 'from-purple-500 to-indigo-500', order: 1 },
    { name: 'Arquitectura', color: 'from-blue-500 to-cyan-500', order: 2 },
    { name: 'Modelo de Datos', color: 'from-emerald-500 to-teal-500', order: 3 },
    { name: 'APIs', color: 'from-orange-500 to-red-500', order: 4 },
    { name: 'Estándares y Esquemas', color: 'from-yellow-500 to-amber-500', order: 5 },
    { name: 'Integraciones', color: 'from-fuchsia-500 to-pink-500', order: 6 },
    { name: 'Gobierno de Datos', color: 'from-slate-500 to-gray-500', order: 7 },
  ];

  const slugify = (text) =>
    text
      .toString()
      .normalize('NFD').replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

  // Cargar carpetas al montar
  useEffect(() => {
    const loadFolders = async () => {
      try {
        const fetched = await foldersService.getFolders();
        // Normalizar a estructura usada por la UI
        const normalized = fetched.map((f) => ({
          id: f.id,
          name: f.name || 'Sin nombre',
          // usar Folder como ícono por defecto
          icon: Folder,
          // color por defecto si no existe en DB
          color: f.color || 'from-purple-500 to-indigo-500',
          order: typeof f.order === 'number' ? f.order : 999, // valor por defecto para ordenamiento
          documentCount: typeof f.documentCount === 'number' ? f.documentCount : 0,
          documents: []
        })).sort((a, b) => a.order - b.order); // ordenar por el campo order
        setFolders(normalized);
      } catch (error) {
        console.error('No se pudieron cargar las carpetas:', error);
      }
    };
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Cargar actividad reciente
  useEffect(() => {
    const loadRecent = async () => {
      try {
        const recents = await documentsService.getRecentDocuments(5);
        setRecentDocuments(recents);
      } catch (error) {
        // Silenciar en UI
      }
    };
    loadRecent();
  }, []);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const created = await foldersService.createFolder({
        name: newFolderName,
        color: 'from-purple-500 to-indigo-500'
      });
      const folderToAdd = {
        id: created.id,
        name: created.name || newFolderName,
        icon: Folder,
        color: created.color || 'from-purple-500 to-indigo-500',
        documentCount: 0,
        documents: []
      };
      setFolders((prev) => [folderToAdd, ...prev]);
      setNewFolderName('');
      setShowNewFolder(false);
    } catch (error) {
      console.error('Error creando carpeta:', error);
    }
  };

  const handleCreateDefaultStructure = async () => {
    if (creatingDefaults) return;
    setCreatingDefaults(true);
    try {
      // Evitar duplicados por slug
      const existingSlugs = new Set(
        folders.map((f) => (f.slug ? f.slug : slugify(f.name)))
      );
      for (const spec of defaultFoldersSpec) {
        const specSlug = slugify(spec.name);
        if (existingSlugs.has(specSlug)) continue;
        await foldersService.createFolder({
          name: spec.name,
          color: spec.color,
          order: spec.order,
          slug: specSlug
        });
        existingSlugs.add(specSlug);
      }
      // Recargar desde Firestore para estado fuente de verdad
      const refreshed = await foldersService.getFolders();
      const normalized = refreshed.map((f) => ({
        id: f.id,
        name: f.name || 'Sin nombre',
        icon: Folder,
        color: f.color || 'from-purple-500 to-indigo-500',
        order: typeof f.order === 'number' ? f.order : undefined,
        slug: f.slug || slugify(f.name || ''),
        documentCount: typeof f.documentCount === 'number' ? f.documentCount : 0,
        documents: []
      })).sort((a, b) => (a.order || 0) - (b.order || 0));
      setFolders(normalized);
    } catch (error) {
      console.error('Error creando estructura base:', error);
    } finally {
      setCreatingDefaults(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta carpeta y todos sus documentos?')) return;
    try {
      await foldersService.deleteFolder(folderId);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      if (selectedFolder?.id === folderId) setSelectedFolder(null);
    } catch (error) {
      console.error('Error eliminando carpeta:', error);
    }
  };

  const handleSaveDocument = async (docData) => {
    if (!selectedFolder) return;
    try {
      // completar datos mínimos
      const payload = {
        ...docData,
        folderId: selectedFolder.id,
        lastUpdated: new Date().toISOString()
      };
      if (docData.id) {
        await documentsService.updateDocument(docData.id, payload);
      } else {
        const created = await documentsService.createDocument(payload);
        payload.id = created.id;
      }
      // recargar documentos de la carpeta seleccionada
      const docs = await documentsService.getDocumentsByFolder(selectedFolder.id);
      // actualizar lista de carpetas con conteo
      const updatedFolders = folders.map((f) =>
        f.id === selectedFolder.id
          ? { ...f, documents: docs, documentCount: docs.length }
          : f
      );
      setFolders(updatedFolders);
      setSelectedFolder({ ...selectedFolder, documents: docs });
      // opcionalmente sincronizar contador en la carpeta
      try { await foldersService.updateDocumentCount(selectedFolder.id, docs.length); } catch {}
    } catch (error) {
      console.error('Error guardando documento:', error);
    } finally {
      setIsEditing(false);
      setEditingDocument(null);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;
    try {
      await documentsService.deleteDocument(docId);
      const docs = await documentsService.getDocumentsByFolder(selectedFolder.id);
      const updatedFolders = folders.map((f) =>
        f.id === selectedFolder.id
          ? { ...f, documents: docs, documentCount: docs.length }
          : f
      );
      setFolders(updatedFolders);
      setSelectedFolder({ ...selectedFolder, documents: docs });
      try { await foldersService.updateDocumentCount(selectedFolder.id, docs.length); } catch {}
    } catch (error) {
      console.error('Error eliminando documento:', error);
    }
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setIsEditing(true);
    setViewingDocument(null);
  };

  const filteredDocuments = selectedFolder?.documents.filter(doc => 
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  ) || [];
  


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 rounded-lg bg-white/10 backdrop-blur-md hover:bg-white/20 transition-colors"
              >
                {isSidebarOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-lg blur opacity-75"></div>
                  <div className="relative bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg p-2">
                    <img 
                      src="/Logo_Final_black.png" 
                      alt="Cromodata Logo" 
                      className="h-8 w-auto object-contain"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                  </div>
                </div>
                <h1 className="text-xl font-bold text-white">Docs</h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {user.name[0].toUpperCase()}
                </div>
                <span className="text-white text-sm font-medium">{user.name}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  user.role === 'admin' 
                    ? 'bg-purple-500/30 text-purple-300' 
                    : 'bg-blue-500/30 text-blue-300'
                }`}>
                  {user.role === 'admin' ? 'Admin' : 'Lector'}
                </span>
              </div>
              
              <button
                onClick={onLogout}
                className="p-2 rounded-lg bg-white/10 backdrop-blur-md hover:bg-white/20 transition-colors group"
              >
                <LogOut className="w-5 h-5 text-white group-hover:text-red-300 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar con carpetas */}
        <aside className={`${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-black/20 backdrop-blur-xl border-r border-white/10 transition-transform duration-300 z-30 overflow-y-auto`}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Carpetas</h2>
              {user.role === 'admin' && (
                <button
                  onClick={() => setShowNewFolder(true)}
                  className="p-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <Plus className="w-4 h-4 text-white" />
                </button>
              )}
            </div>

            {showNewFolder && (
              <div className="mb-4 p-3 bg-white/10 rounded-lg">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Nombre de carpeta"
                  autoFocus
                />
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={handleCreateFolder}
                    className="flex-1 px-2 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
                  >
                    Crear
                  </button>
                  <button
                    onClick={() => {
                      setShowNewFolder(false);
                      setNewFolderName('');
                    }}
                    className="flex-1 px-2 py-1 bg-white/10 text-white rounded text-sm hover:bg-white/20"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <nav className="space-y-2">
              {folders.map((folder) => {
                const Icon = folder.icon;
                const isActive = selectedFolder?.id === folder.id;
                return (
                  <div
                    key={folder.id}
                    className={`group rounded-xl transition-all duration-200 ${
                      isActive 
                        ? 'bg-white/20 backdrop-blur-md shadow-lg' 
                        : 'hover:bg-white/10 backdrop-blur-md'
                    }`}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={async () => {
                        // seleccionar y cargar documentos de la carpeta
                        setSelectedFolder({ ...folder, documents: [] });
                        setIsSidebarOpen(false);
                        try {
                          const docs = await documentsService.getDocumentsByFolder(folder.id);
                          setSelectedFolder({ ...folder, documents: docs });
                          setFolders((prev) => prev.map((f) => (
                            f.id === folder.id ? { ...f, documents: docs, documentCount: docs.length } : f
                          )));
                        } catch (error) {
                          console.error('No se pudieron cargar documentos:', error);
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                      className="w-full flex items-center space-x-3 px-4 py-3 cursor-pointer"
                    >
                      <div className={`p-2 rounded-lg bg-gradient-to-r ${folder.color}`}>
                        {isActive ? <FolderOpen className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white" />}
                      </div>
                      <span className={`font-medium flex-1 text-left ${isActive ? 'text-white' : 'text-gray-300'}`}>
                        {folder.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {typeof folder.documentCount === 'number' ? folder.documentCount : (folder.documents?.length || 0)}
                      </span>
                      {user.role === 'admin' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 transition-all"
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </nav>

            <div className="mt-8 p-4 bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-md rounded-xl border border-white/10">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-xs font-semibold text-white">Pro Tip</span>
              </div>
              <p className="text-xs text-gray-300">
                {user.role === 'admin' 
                  ? 'Puedes crear, editar y eliminar documentos'
                  : 'Tienes acceso de solo lectura a los documentos'}
              </p>

            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          {selectedFolder ? (
            <>
              {/* Header de la carpeta */}
              <div className="max-w-4xl mx-auto mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${selectedFolder.color}`}>
                      <FolderOpen className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedFolder.name}</h2>
                      <p className="text-sm text-gray-400">
                        {selectedFolder.documents?.length || 0} {(selectedFolder.documents?.length || 0) === 1 ? 'documento' : 'documentos'}
                      </p>
                    </div>
                  </div>
                  {user.role === 'admin' && (
                    <button
                      onClick={() => {
                        setEditingDocument(null);
                        setIsEditing(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all"
                    >
                      <Plus className="w-5 h-5" />
                      <span>Nuevo Documento</span>
                    </button>
                  )}
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar en esta carpeta..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Lista de documentos */}
              <div className="max-w-4xl mx-auto">
                {filteredDocuments.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-md rounded-full mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-400">
                      {searchQuery ? 'No se encontraron documentos' : 'Esta carpeta está vacía'}
                    </p>
                    {!searchQuery && user.role === 'admin' && (
                      <button
                        onClick={() => {
                          setEditingDocument(null);
                          setIsEditing(true);
                        }}
                        className="mt-4 px-4 py-2 bg-purple-500/20 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-all"
                      >
                        Crear primer documento
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="group bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-5 hover:bg-white/10 transform hover:scale-[1.01] transition-all duration-300"
                      >
                        <div className="flex items-start justify-between">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => setViewingDocument(doc)}
                          >
                            <h3 className="font-semibold text-white mb-1 group-hover:text-purple-300 transition-colors">
                              {doc.title}
                            </h3>
                            <p className="text-sm text-gray-400 mb-3">{doc.description}</p>
                            <div className="flex items-center space-x-3 text-xs text-gray-500">
                              <span className="flex items-center">
                                <User className="w-3 h-3 mr-1" />
                                {doc.author}
                              </span>
                              <span>•</span>
                              <span className="flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {safeFormatDate(doc.lastUpdated)}
                              </span>
                              {doc.tags && doc.tags.length > 0 && (
                                <>
                                  <span>•</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {doc.tags.map(tag => (
                                      <span key={tag} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full">
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          {user.role === 'admin' && (
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleEditDocument(doc)}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Edit className="w-4 h-4 text-white" />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="p-2 rounded-lg bg-white/10 hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Estado inicial sin carpeta seleccionada */
            <div className="max-w-4xl mx-auto">
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-md rounded-full mb-6">
                  <Folder className="w-10 h-10 text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Bienvenido a Cromodata Docs</h2>
                <p className="text-gray-400 mb-8">
                  Selecciona una carpeta del menú lateral para ver los documentos
                </p>
                
                {/* Quick Stats */}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
                    <div className="flex items-center justify-center mb-3">
                      <Folder className="w-8 h-8 text-purple-400" />
                    </div>
                    <div className="text-2xl font-bold text-white">{folders.length}</div>
                    <div className="text-sm text-gray-400">Carpetas</div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
                    <div className="flex items-center justify-center mb-3">
                      <FileText className="w-8 h-8 text-blue-400" />
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {folders.reduce((acc, folder) => acc + (folder.documentCount || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-400">Documentos</div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
                    <div className="flex items-center justify-center mb-3">
                      <Users className="w-8 h-8 text-green-400" />
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {user.role === 'admin' ? 'Admin' : 'Lector'}
                    </div>
                    <div className="text-sm text-gray-400">Tu rol</div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="mt-12">
                  <h3 className="text-lg font-semibold text-white mb-4">Actividad Reciente</h3>
                  <div className="space-y-3">
                    {recentDocuments.map(doc => {
                      const folder = folders.find(f => f.id === doc.folderId);
                      return (
                      <div
                        key={doc.id}
                        className="flex items-center space-x-4 p-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl hover:bg-white/10 transition-all cursor-pointer"
                        onClick={() => {
                          if (folder) {
                            setSelectedFolder({ ...folder, documents: [] });
                            setViewingDocument(doc);
                          }
                        }}
                      >
                        <div className={`p-2 rounded-lg bg-gradient-to-r ${folder?.color || 'from-purple-500 to-indigo-500'}`}>
                          <FileText className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white font-medium">{doc.title}</p>
                          <p className="text-xs text-gray-400">
                            {(folder?.name) || 'Carpeta'} • {doc.author || '—'} • {safeFormatDate(doc.lastUpdated)}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Editor Modal */}
      {isEditing && (
        <MarkdownEditor
          document={editingDocument}
          onSave={handleSaveDocument}
          onCancel={() => {
            setIsEditing(false);
            setEditingDocument(null);
          }}
          user={user}
        />
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewer
          document={viewingDocument}
          onClose={() => setViewingDocument(null)}
          onEdit={handleEditDocument}
          user={user}
        />
      )}
    </div>
  );
};

export default DocumentationPage;
